import { Link } from "react-router-dom";
import { Activity, AlertCircle, ExternalLink, ArrowRight, AlertTriangle, Loader2, RefreshCw, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/useAuthStore";
import { useAdminHealth, useRefreshHealth } from "@/hooks/useAdminUsers";
import type { HealthSignal } from "@/api/adminCenter";

/**
 * D-044 S4 — #7 System Health (VISUAL v1.1: P3 cached snapshot +
 * lastComputedAt + «Оновити»; MP1 refresh-failure → error-банер, попередній
 * snapshot НЕ як свіжий + «Повторити»; MP5 картка read-only, навігує лише
 * action-link; MP6 all-green нейтрально + «6/6 OK»). 6 sourced signals,
 * 0 fake-green (AC-060/061); severity/links = backend-owned (D-017).
 */

export function AdminHealthPage() {
  const perms = useAuthStore((s) => s.permissions);
  const canView = !!perms?.canViewAdminCenter;
  const healthQ = useAdminHealth(canView);
  const refreshM = useRefreshHealth();

  if (!canView) {
    return (
      <div className="p-4 md:p-6">
        <div className="mx-auto w-full max-w-[640px] rounded-lg border bg-muted/30 p-8 text-center">
          <ShieldAlert className="mx-auto mb-2 h-5 w-5 text-muted-foreground" />
          <p className="text-sm font-medium">Немає доступу до Адмін-центру</p>
          <p className="mt-1 text-xs text-muted-foreground">Розділ доступний лише адміністраторам.</p>
        </div>
      </div>
    );
  }

  const data = healthQ.data;
  const snap = data?.snapshot;
  const refreshFailed = refreshM.isError;

  return (
    <div className="p-4 md:p-6">
      <div className="mx-auto w-full max-w-[1100px] space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
              <Activity className="h-5 w-5" /> Стан системи
            </h1>
            <p className="mt-0.5 text-sm text-muted-foreground">
              6 операційних сигналів · кешований знімок (P3) · read-only.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={refreshM.isPending}
            onClick={() => refreshM.mutate()}
          >
            {refreshM.isPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-1 h-4 w-4" />
            )}
            Оновити
          </Button>
        </div>

        <div className="rounded-card border bg-card p-4">
          <div className="mb-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            {snap && (
              <>
                <span>
                  Оновлено <span className="tabular-nums">{snap.lastComputedAt}</span> · cached snapshot
                </span>
                {/* MP6: нейтральний summary */}
                <span className="rounded-badge bg-muted px-2 py-0.5">
                  {snap.summary.ok}/{snap.summary.total} OK
                  {snap.summary.error > 0 && ` · ${snap.summary.error} помилка`}
                  {snap.summary.warning > 0 && ` · ${snap.summary.warning} увага`}
                </span>
                {data?.computeError && (
                  <span className="rounded-badge bg-warning px-2 py-0.5 text-warning-foreground">
                    останній автоперерахунок не вдався — знімок застарілий
                  </span>
                )}
              </>
            )}
          </div>

          {/* MP1: failure-банер — попередній snapshot НЕ подається як свіжий */}
          {refreshFailed && (
            <div className="mb-3 flex items-start gap-2 rounded-lg bg-destructive-soft px-3 py-2.5 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <b>Не вдалося оновити.</b> Показано попередній знімок
                {snap ? ` (${snap.lastComputedAt})` : ""} — він не подається як свіжий.{" "}
                <button type="button" className="underline" onClick={() => refreshM.mutate()}>
                  Повторити
                </button>
              </span>
            </div>
          )}

          {healthQ.isLoading && (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          )}
          {healthQ.isError && (
            <div className="p-4 text-sm text-destructive">
              Не вдалося завантажити стан системи.{" "}
              <button type="button" className="underline" onClick={() => healthQ.refetch()}>
                Повторити
              </button>
            </div>
          )}

          {snap && (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {snap.signals.map((s) => (
                <SignalCard key={s.key} signal={s} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SignalCard({ signal: s }: { signal: HealthSignal }) {
  return (
    // MP5: картка read-only — клікабельний ЛИШЕ action-link
    <div className="rounded-lg border bg-card p-3" title={`Джерело: ${s.source}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <span
            className={cn(
              "h-2.5 w-2.5 shrink-0 rounded-full",
              s.severity === "ok" && "bg-success-foreground",
              s.severity === "warning" && "bg-warning-foreground",
              s.severity === "error" && "bg-destructive",
            )}
          />
          <span className="truncate text-sm font-medium">{s.label}</span>
        </div>
        {s.severity === "ok" && (
          <span className="rounded-badge bg-success px-2 py-0.5 text-xs text-success-foreground">OK</span>
        )}
        {s.severity === "warning" && (
          <span className="rounded-badge bg-warning px-2 py-0.5 text-xs text-warning-foreground">Увага</span>
        )}
        {s.severity === "error" && (
          <span className="rounded-badge bg-destructive-soft px-2 py-0.5 text-xs text-destructive">Помилка</span>
        )}
      </div>
      <p className="mt-1.5 text-xs text-muted-foreground">{s.value}</p>
      {s.warnNote && (
        <p className="mt-1.5">
          <span className="inline-flex items-center gap-1 rounded-badge bg-warning px-2 py-0.5 text-[11px] text-warning-foreground">
            <AlertTriangle className="h-3 w-3" /> {s.warnNote}
          </span>
        </p>
      )}
      {s.link &&
        (s.link.external && s.link.href ? (
          <a
            href={s.link.href}
            target="_blank"
            rel="noreferrer"
            className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary underline-offset-2 hover:underline"
          >
            <ExternalLink className="h-3 w-3" /> {s.link.label}
          </a>
        ) : s.link.to ? (
          <Link
            to={s.link.to}
            className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary underline-offset-2 hover:underline"
          >
            <ArrowRight className="h-3 w-3" /> {s.link.label}
          </Link>
        ) : null)}
    </div>
  );
}
